import { Col, Divider, Row } from 'antd';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import CustomModal from '../../../../common-components/CustomModal';
import useStore from '../../../../hooks/useStore';
import {
  ContentSmallCamptonBook,
  H8CamptonSemiBold19,
  SmallGrey12
} from '../../../../layout/globalFonts';
import { ReactComponent as ConfirmIcon } from '../../../../layout/icons/green-circle-with-checkmark.svg';
import { ReactComponent as RedErrorNotification } from '../../../../layout/icons/red-error-notification.svg';
import LoadingSpinnerAnimated from '../../../../layout/icons/loading-spinner-animated.gif';
import ContentStore from '../../../../stores/ContentStore';
import LoadingStore from '../../../../stores/LoadingStore';
import ModalStore from '../../../../stores/ModalStore';
import ProductStore from '../../../../stores/ProductStore';
import {
  Country,
  LicensePlateValidationResult,
  ProductInfo,
  ProductInfoPaginatedList,
  Vehicle
} from '../../../../swagger/api';
import { validateLicensePlate } from '../../../../utils/formValidators';
import { BrobizzCheckbox } from '../../../../common-components/Selectors';
import CouldNotLookUpLicensePlateModal from '../../../modals/CouldNotLookUpLicensePlateModal';
import LicensePlateAlreadyRegisteredModal from '../../../modals/LicensePlateAlreadyRegisteredModal';
import { LicensePlateField } from '../components/LicensePlateField';
import { MinKontoContent } from '../../../../stores/content-stores/CMSStore';

interface PublicCustomerWantsToEditAnprLicensePlateProps {
  productInfo: ProductInfo;
}

enum CmsContent {
  LicensePlateInputLabel = 'YouWantToEditLicensePlateInputLabel',
  ModalCancelation = 'Undo',
  ModalConfirmation = 'YouWantToEditLicensePlateModalConfirmation',
  ModalContent = 'YouWantToEditLicensePlateModalContent',
  ModalDisclaimer = 'YouWantToEditLicensePlateDisclaimer',
  ModalTitle = 'YouWantToEditLicensePlateModalTitle',
  UpdateBrobizz = 'UpdatingBrobizzSystems',
  ChangingNumberPlate = 'ChangingNumberPlate',
  TransferAgreements = 'TransferingDiscountAgreements',
  FinishUpdate = 'FinishingUpdatingLicensePlate',
  EditedLicensePlate = 'YouHaveEditedLicensePlate',
  DeleteError = 'DeleteOldLicensePlateError',
  DeleteErrorSubtext = 'DeleteOldLicensePlateErrorSubText',
  EditError = 'EditLicensePlateError',
  EditErrorSubtext = 'EditLicensePlateErrorSubText'
}

const Loader = styled.img`
  width: 100px;
  height: 100px;
  margin: 20px 0;
`;

const StyledDivider = styled(Divider)`
  height: 1px;
  background-color: #dedede;
`;

const StyledConfirmIcon = styled(ConfirmIcon)`
  width: 56px;
  height: 56px;
`;

const StyledErrorIcon = styled(RedErrorNotification)`
  width: 56px;
  height: 56px;
`;

const PublicCustomerWantsToEditAnprLicensePlate: React.FC<
  PublicCustomerWantsToEditAnprLicensePlateProps
> = ({ productInfo }) => {
  const { cmsStore } = useStore(ContentStore) as ContentStore;
  const loadingStore = useStore(LoadingStore) as LoadingStore;
  const modalStore = useStore(ModalStore) as ModalStore;
  const { productVehicleStore, productCardStore, productItemsStore } = useStore(
    ProductStore
  ) as ProductStore;

  const [isLoading, setLoading] = React.useState(false);
  const [isConfirmed, setConfirmation] = React.useState(false);
  const [licensePlate, setLicensePlate] = React.useState('');
  const licensePlateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateStatusTimeout = 4000;
  const updateAnprLicensePlateStatus = {
    UPDATE: cmsStore.getContent(CmsContent.UpdateBrobizz)?.content!,
    TRANSFER: cmsStore.getContent(CmsContent.TransferAgreements)?.content!,
    SOON: cmsStore.getContent(CmsContent.FinishUpdate)?.content!
  };

  const openUpdateModal = () => {
    modalStore.closeModal();
    modalStore.openModal(
      <CustomModal
        header={cmsStore.getContent(CmsContent.ChangingNumberPlate)?.content}
        closable={false}
        maxWidth
        flatten
        contextTexts={Object.values(updateAnprLicensePlateStatus)}
        contextTextsTimeout={updateStatusTimeout}
        onGreenLinkClick={() => {
          productCardStore.closeModal();
          modalStore.closeModal();
        }}
      >
        <Row style={{ justifyContent: 'center' }}>
          <Row style={{ width: '100%', justifyContent: 'center' }}>
            <Loader src={LoadingSpinnerAnimated} />
          </Row>
        </Row>
      </CustomModal>
    );
  };

  const openNotificationModal = (content?: MinKontoContent) => {
    modalStore.openModal(
      <CustomModal
        topIconDesktop={<ConfirmIcon />}
        topIconMobile={<StyledConfirmIcon />}
        isHeaderRed={false}
        header={content?.content}
        contentText={content?.subText}
        primaryButtonText={cmsStore.getContentString('Ok')}
        closable
        modalBodyCustomPadding="0"
        primaryButtonCustomMargin="0 0 3% 0"
        onPrimaryButtonClick={() => {
          modalStore.closeModal();
        }}
      />
    );
  };

  const openRedErrorNotificationModal = (headerText?: string, contentText?: string) => {
    modalStore.openModal(
      <CustomModal
        topIconDesktop={<RedErrorNotification />}
        topIconMobile={<StyledErrorIcon />}
        isHeaderRed
        header={headerText}
        contentText={contentText}
        primaryButtonText={cmsStore.getContentString('Ok')}
        closable
        modalBodyCustomPadding="0"
        primaryButtonCustomMargin="0 0 3% 0"
        onPrimaryButtonClick={() => {
          productCardStore.closeModal();
          modalStore.closeModal();
        }}
      />
    );
  };

  const callUpdateModal = async () => {
    openUpdateModal();
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, Object.values(updateAnprLicensePlateStatus).length * updateStatusTimeout);
    });
  };

  const updateAnprLicensePlate = () => {
    const identifierId = productInfo.identifierId!;
    const licensePlateValue = productVehicleStore.formAutoFill?.licensePlate!;
    const isoCodeValue = productVehicleStore.formAutoFill?.isoCode!;
    const countryId = cmsStore.rootStore.contentStore.genericContentStore.allCountries.find(
      (country: Country) => country.isoCode === isoCodeValue
    )?.id!;

    const vehicle: Vehicle = {
      plate: licensePlateValue,
      brand: productVehicleStore.carBrand,
      model: productVehicleStore.carModel,
      color: productVehicleStore.carColor
    };

    setLoading(true);
    return callUpdateModal().then(async () => {
      productCardStore
        .updateAnprLicensePlate(identifierId, countryId, vehicle)
        .then(async (result) => {
          const list = (await productItemsStore.getProducts()) as ProductInfoPaginatedList;
          modalStore.closeModal();
          if (result.closedOldAnpr && result.newAnprCreated) {
            setLoading(false);
            setConfirmation(false);
            productVehicleStore.cleanStore();
            const newProductIdentifierId = list.paginatedItems?.find(
              (product) => product.licensePlate === licensePlateValue
            )?.identifierId;
            productCardStore.setIsAnimationDisabled(true);
            productCardStore.setProductIdOfOpenProductCard(newProductIdentifierId!);
            productCardStore.setIsAnimationDisabled(false);
            openNotificationModal(cmsStore.getContent(CmsContent.EditedLicensePlate));
          } else if (!result.closedOldAnpr && result.newAnprCreated) {
            openRedErrorNotificationModal(
              cmsStore.getErrorMessage(CmsContent.DeleteError),
              cmsStore.getErrorMessage(CmsContent.DeleteErrorSubtext)
            );
          } else {
            openRedErrorNotificationModal(
              cmsStore.getErrorMessage(CmsContent.EditError),
              cmsStore.getErrorMessage(CmsContent.EditErrorSubtext)
            );
          }
        });
    });
  };

  const onNewLicensePlateChange = (countryCode: string, licensePlateValue: string) => {
    if (licensePlateTimeoutRef.current) {
      clearTimeout(licensePlateTimeoutRef.current);
    }

    if (!validateLicensePlate(countryCode, licensePlateValue)) {
      return;
    }

    licensePlateTimeoutRef.current = setTimeout(() => {
      loadingStore.enableGlobalSpinner();
      productVehicleStore
        .isLicenseplateAlreadyRegistered(licensePlateValue, countryCode.toUpperCase())
        .then(() => {
          if (
            productVehicleStore.isPlateRegistered === LicensePlateValidationResult.NotReferenced
          ) {
            productVehicleStore
              .getVehicleCardFromEpass(licensePlateValue, countryCode.toUpperCase())
              .catch((error) => {
                loadingStore.disableGlobalSpinner();
                if (error.response?.data.errorCode.includes('vehicleNotFoundErrorCode')) {
                  modalStore.openModal(
                    <CouldNotLookUpLicensePlateModal
                      licensePlate={licensePlateValue}
                      onCancel={() => {
                        productVehicleStore.cleanStore();
                        setLicensePlate('');
                      }}
                      onConfirmation={() => {
                        productVehicleStore.overrideAutoFill({
                          licensePlate,
                          isoCode: countryCode
                        });
                      }}
                    />
                  );
                }
              });
          } else {
            modalStore.openModal(
              <LicensePlateAlreadyRegisteredModal
                licensePlate={licensePlate}
                onConfirmation={() => {
                  setLicensePlate('');
                  productVehicleStore.cleanStore();
                }}
              />
            );
          }
        })
        .finally(() => {
          loadingStore.disableGlobalSpinner();
        });
    }, 1500);
  };

  return (
    <CustomModal
      header={cmsStore.getContentString(CmsContent.ModalTitle)}
      primaryButtonText={
        cmsStore.getContentString(CmsContent.ModalConfirmation) ?? 'Change license plate'
      }
      greenLinkText={cmsStore.getContentString(CmsContent.ModalCancelation)}
      closable
      isLoading={isLoading}
      isPrimaryButtonDisabled={
        productVehicleStore.formAutoFill?.licensePlate == null || !isConfirmed
      }
      modalBodyCustomPadding="24px 24px 12px 24px"
      primaryButtonCustomMargin="10px 0 5% 0"
      onPrimaryButtonClick={() => updateAnprLicensePlate()}
      onCloseClick={() => productVehicleStore.cleanStore()}
      mobileViewSecondaryButton
      addPaddingTop
      onGreenLinkClick={() => {
        productVehicleStore.cleanStore();
        modalStore.closeModal();
      }}
    >
      <Row style={{ width: '100%', justifyContent: 'center' }}>
        <Row style={{ width: '100%', flexDirection: 'column', alignItems: 'center' }}>
          <Row style={{ marginBottom: '27px', marginTop: '9.5px' }}>
            <ContentSmallCamptonBook>
              {cmsStore.getContentString(CmsContent.ModalContent, [
                ['{identifierNumber}', productInfo.licensePlate!]
              ])}
            </ContentSmallCamptonBook>
          </Row>
          <Row style={{ maxWidth: '280px', margin: '0 0 20px 0', justifyContent: 'center' }}>
            <Row style={{ width: '100%', textAlign: 'left' }}>
              <H8CamptonSemiBold19>
                {cmsStore.getContentString(CmsContent.LicensePlateInputLabel)}
              </H8CamptonSemiBold19>
            </Row>
            <Row style={{ width: '100%' }}>
              <LicensePlateField
                licensePlate={licensePlate}
                setNewLicensePlate={setLicensePlate}
                disabled={isLoading || loadingStore.showGlobalSpinner}
                onChange={(countryCode, licensePlateValue) =>
                  onNewLicensePlateChange(countryCode, licensePlateValue)
                }
              />
            </Row>
          </Row>
          {productVehicleStore.formAutoFill && (
            <>
              <Row
                justify="space-evenly"
                style={{
                  margin: '0 0 20px 0',
                  justifyContent: 'flex-start',
                  flexWrap: 'nowrap',
                  width: '100%',
                  maxWidth: '280px'
                }}
              >
                <Col style={{ flex: 1 }}>
                  <Row style={{ marginBottom: 10 }}>
                    <SmallGrey12>{cmsStore.getContent('Model')?.content}</SmallGrey12>
                  </Row>
                  <Row style={{ whiteSpace: 'nowrap' }}>
                    {productVehicleStore.carBrand
                      ? `${productVehicleStore.carBrand} ${productVehicleStore.carModel}`
                      : '-'}
                  </Row>
                </Col>
                <Col style={{ flex: 1 }}>
                  <Row style={{ marginBottom: 10 }}>
                    <SmallGrey12>{cmsStore.getContent('Color')?.content}</SmallGrey12>
                  </Row>
                  <Row style={{ whiteSpace: 'nowrap' }}>
                    {productVehicleStore.carColor ? productVehicleStore.carColor : '-'}
                  </Row>
                </Col>
              </Row>
              <StyledDivider />
              <Row justify="start" style={{ marginBottom: '40px', textAlign: 'left' }}>
                <BrobizzCheckbox
                  checked={isConfirmed}
                  onChange={(e) => setConfirmation(e.target.checked)}
                >
                  <Row justify="start" style={{ marginBottom: '10px' }}>
                    <ContentSmallCamptonBook>
                      {cmsStore.getContent(CmsContent.ModalDisclaimer)?.content}
                    </ContentSmallCamptonBook>
                  </Row>
                  <Row justify="start">
                    <ContentSmallCamptonBook>
                      {cmsStore.getContent(CmsContent.ModalDisclaimer)?.subText}
                    </ContentSmallCamptonBook>
                  </Row>
                </BrobizzCheckbox>
              </Row>
            </>
          )}
        </Row>
      </Row>
    </CustomModal>
  );
};

export default observer(PublicCustomerWantsToEditAnprLicensePlate);
